// src/components/StaffManager.js
// Owner-only: create / re-role / deactivate staff logins (cashier, manager).
// Staff sign in at /staff. Uses the shared owner `api` client.

import React, { useEffect, useState, useCallback } from 'react';
import ConfirmModal from './ConfirmModal';
import api from '../services/api';

const ROLE_HELP = {
  CASHIER: 'Works orders; marks items sold out and adjusts stock',
  MANAGER: 'Everything a cashier can, plus editing menu items and prices',
  OWNER:   'Full access to the vendor dashboard',
};
const EMPTY = { name: '', email: '', role: 'CASHIER', password: '' };

export default function StaffManager() {
  const [confirm, setConfirm] = useState(null); // { message, onConfirm } for ConfirmModal
  const [staff, setStaff] = useState(null);
  const [form,  setForm]  = useState(EMPTY);
  const [msg,   setMsg]   = useState('');
  const [busy,  setBusy]  = useState(false);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };
  const load = useCallback(async () => {
    try { setStaff((await api.get('/vendors/me/staff')).data); }
    catch (err) { setStaff([]); flash('❌ ' + (err.response?.data?.error || 'Could not load staff')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.email.trim() || form.password.length < 8) return flash('❌ Email and a password of at least 8 characters are required');
    setBusy(true);
    try {
      await api.post('/vendors/me/staff', { ...form, email: form.email.trim().toLowerCase() });
      setForm(EMPTY); flash('✅ Staff member added — share their email and password; they sign in at /staff');
      load();
    } catch (err) { flash('❌ ' + (err.response?.data?.error || 'Could not add staff member')); }
    finally { setBusy(false); }
  };
  const patch = async (s, data, okText) => {
    try { await api.patch(`/vendors/me/staff/${s.id}`, data); flash(okText); load(); }
    catch (err) { flash('❌ ' + (err.response?.data?.error || 'Update failed')); }
  };
  const deactivate = async (s) => {
    try { await api.delete(`/vendors/me/staff/${s.id}`); flash('✅ Deactivated'); load(); }
    catch (err) { flash('❌ ' + (err.response?.data?.error || 'Update failed')); }
  };

  return (
    <div className="page">
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <div className="page-title" style={{ marginBottom: 4 }}>👥 Staff</div>
      <div className="page-sub" style={{ marginBottom: 20 }}>
        Staff sign in at <strong>{window.location.origin}/staff</strong> with their own email and password.
      </div>
      {msg && <div className="card" style={{ marginBottom: 14, padding: '10px 14px' }}>{msg}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 14 }}>Add staff member</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 8 }}>
          <input placeholder="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <input type="email" placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <input type="password" placeholder="Initial password (min 8)" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
          <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
            <option value="CASHIER">Cashier</option>
            <option value="MANAGER">Manager</option>
            <option value="OWNER">Owner (full access)</option>
          </select>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>{ROLE_HELP[form.role]}</div>
        <button className="btn btn-primary" onClick={create} disabled={busy}>Add Staff</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {staff === null && <div style={{ padding: 16 }}>Loading...</div>}
        {staff && staff.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>No staff yet</div>}
        {(staff || []).map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', opacity: s.isActive ? 1 : .55 }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontWeight: 600 }}>{s.name || s.email}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>{s.email}</div>
            </div>
            <select value={s.role} disabled={!s.isActive} style={{ width: 130 }}
              onChange={e => patch(s, { role: e.target.value }, '✅ Role updated')}>
              <option value="CASHIER">Cashier</option>
              <option value="MANAGER">Manager</option>
              <option value="OWNER">Owner</option>
            </select>
            {s.isActive
              ? <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ message: `Deactivate ${s.name || s.email}? They will be signed out immediately.`, confirmLabel: 'Deactivate', onConfirm: () => deactivate(s) })}>Deactivate</button>
              : <button className="btn btn-primary btn-sm" onClick={() => patch(s, { isActive: true }, '✅ Reactivated')}>Reactivate</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
